import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Store,
  Gift,
  BarChart3,
  Monitor,
  ListChecks,
  Megaphone,
  CreditCard,
  TrendingUp,
  Settings,
  LogOut,
  Sparkles,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  RefreshCw,
  Loader2,
  Calendar,
  MapPin,
  Users,
  Wallet,
  DollarSign,
  Package
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import axios from "axios";
import { toast } from "sonner";

const API = '/api';

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
  { icon: FileText, label: "Registrations", path: "/admin/registrations" },
  { icon: AlertTriangle, label: "OCR Exceptions", path: "/admin/ocr-exceptions" },
  { icon: Store, label: "Retailers", path: "/admin/retailers" },
  { icon: Gift, label: "Rewards", path: "/admin/rewards" },
  { icon: Sparkles, label: "Ayr Miles Audit", path: "/admin/entitlements" },
  { icon: BarChart3, label: "Analytics", path: "/admin/analytics" },
  { icon: Monitor, label: "Screens", path: "/admin/screens" },
  { icon: ListChecks, label: "Playlists", path: "/admin/playlists" },
  { icon: Megaphone, label: "Advertisers", path: "/admin/advertisers" },
  { icon: TrendingUp, label: "Campaigns", path: "/admin/campaigns" },
  { icon: CreditCard, label: "Billing", path: "/admin/billing" },
  { icon: TrendingUp, label: "Revenue", path: "/admin/revenue" },
  { icon: Wallet, label: "Commercial", path: "/admin/commercial" },
  { icon: DollarSign, label: "Finance Centre", path: "/admin/commercial/finance" },
  { icon: Package, label: "Products & Tariffs", path: "/admin/commercial/products-tariffs" },
  { icon: Settings, label: "Settings", path: "/admin/settings" },
];

export default function AdminEntitlements() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entitlements, setEntitlements] = useState([]);
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [entitlementsRes, statsRes, sitesRes] = await Promise.all([
        axios.get(`${API}/entitlements/list`).catch(() => ({ data: [] })),
        axios.get(`${API}/entitlements/stats`),
        axios.get(`${API}/sites`, { withCredentials: true })
      ]);
      
      setEntitlements(entitlementsRes.data || []);
      setStats(statsRes.data);
      setSites(sitesRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    document.cookie = "admin_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    navigate("/admin");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "available":
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" />
            Active
          </span>
        );
      case "consumed":
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            <CheckCircle className="w-3 h-3" />
            Used
          </span>
        );
      case "expired":
        return (
          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
            <XCircle className="w-3 h-3" />
            Expired
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
            {status}
          </span>
        );
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.site_id === siteId);
    return site?.name || siteId;
  };

  const filteredEntitlements = entitlements.filter(ent => {
    const matchesSearch = !searchTerm || 
      ent.vehicle_registration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ent.qualifying_retailer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || ent.status === statusFilter;
    const matchesSite = siteFilter === "all" || ent.site_id === siteFilter;
    
    return matchesSearch && matchesStatus && matchesSite;
  });

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-entitlements">
      {/* Sidebar */}
      <div className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #2563EB 0%, #10B981 100%)' }}
            >
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">ParkWise</h1>
              <p className="text-xs text-slate-500">Admin Console</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === "/admin/entitlements";
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-purple-500/20 text-purple-300"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <Sparkles className="w-8 h-8 text-purple-400" />
                Ayr Miles Entitlements
              </h1>
              <p className="text-slate-400 mt-1">
                Audit trail for parking entitlements earned through partner visits
              </p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats.today?.available || 0}</p>
                      <p className="text-sm text-slate-400">Active Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Gift className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats.today?.consumed || 0}</p>
                      <p className="text-sm text-slate-400">Used Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats.all_time?.consumed || 0}</p>
                      <p className="text-sm text-slate-400">Total Used</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-700 flex items-center justify-center">
                      <XCircle className="w-6 h-6 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats.all_time?.expired || 0}</p>
                      <p className="text-sm text-slate-400">Expired Unused</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card className="bg-slate-900 border-slate-800 mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      placeholder="Search by vehicle or retailer..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    />
                  </div>
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-white">All Status</SelectItem>
                    <SelectItem value="available" className="text-white">Active</SelectItem>
                    <SelectItem value="consumed" className="text-white">Used</SelectItem>
                    <SelectItem value="expired" className="text-white">Expired</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={siteFilter} onValueChange={setSiteFilter}>
                  <SelectTrigger className="w-[200px] bg-slate-800 border-slate-700 text-white">
                    <MapPin className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Site" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-white">All Sites</SelectItem>
                    {sites.map(site => (
                      <SelectItem key={site.site_id} value={site.site_id} className="text-white">
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Entitlements Table */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Entitlement Audit Log
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                </div>
              ) : filteredEntitlements.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No entitlements found</p>
                  <p className="text-slate-500 text-sm mt-1">
                    Entitlements are created when customers scan POS QR codes at Ayr Miles partner retailers
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Vehicle
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Partner Retailer
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Used
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Value
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {filteredEntitlements.map((ent) => (
                        <tr key={ent.entitlement_id} className="hover:bg-slate-800/30 transition-colors">
                          <td className="px-6 py-4">
                            <span className="font-mono font-semibold text-white">
                              {ent.vehicle_registration}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-white font-medium">{ent.qualifying_retailer_name || "—"}</p>
                              <p className="text-xs text-slate-500">{getSiteName(ent.site_id)}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-slate-300">
                            {formatDateTime(ent.created_at)}
                          </td>
                          <td className="px-6 py-4 text-slate-300">
                            {ent.consumed_at ? formatDateTime(ent.consumed_at) : "—"}
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-green-400 font-semibold">
                              {ent.free_hours}hr free
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {getStatusBadge(ent.status)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
